"use client";

import { c as _c } from "react/compiler-runtime";
import { motion, useAnimation, type AnimationControls } from "framer-motion";
import { useEffect } from "react";
import MenuButton from "@/components/ui/MenuButton";
const SESSION_KEY = "hasSeenHeroAnimation";
function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // ignore
  }
}
const MENU_ITEMS = [{
  label: "EVENTY",
  href: "/eventy"
}, {
  label: "WYJAZDY",
  href: "/wyjazdy"
}, {
  label: "KONFERENCJE",
  href: "/konferencje"
}, {
  label: "ATRAKCJE",
  href: "/atrakcje"
}] as const;
type HeroData = {
  companyName?: string;
  leftTitle?: string;
  rightTitle?: string;
};
type Props = {
  hero: HeroData;
  videoControls: AnimationControls;
  menuControls: AnimationControls;
};
const ROOT_CLASS = "w-full h-full flex flex-col justify-start items-center relative overflow-hidden pt-[2vh]";
const WRAPPER_CLASS = "w-full flex flex-col items-center z-30 will-change-transform";
const H1_CLASS = "font-bold uppercase text-primary leading-[0.8] text-[15vw] sm:text-[13vw] md:text-[14vw] lg:text-[15vw] tracking-[-0.03em] text-center whitespace-nowrap select-none";
const TITLES_ROW_CLASS = "flex items-center justify-between uppercase font-medium text-primary text-[10px] md:text-sm tracking-[0.2em] opacity-90";
const NAV_CLASS = "hidden [@media(hover:hover)_and_(pointer:fine)]:block absolute bottom-10 z-40";
const UL_CLASS = "flex items-center justify-center gap-8 uppercase tracking-[0.15em] text-primary font-bold";
function nextFrame(): Promise<void> {
  return new Promise(resolve => requestAnimationFrame(() => resolve()));
}
export default function HeroTitleGlitch(t0) {
  const $ = _c(32);
  const {
    hero,
    videoControls,
    menuControls
  } = t0;
  const textControls = useAnimation();
  let t1;
  let t2;
  if ($[0] !== menuControls || $[1] !== textControls || $[2] !== videoControls) {
    t1 = () => {
      let cancelled = false;
      const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
      const initialY = isMobile ? "90dvh" : "72dvh";
      const runSequence = async () => {
        const hasSeen = safeSessionGet(SESSION_KEY);
        const finalPos = {
          y: 0,
          opacity: 1
        };
        if (hasSeen) {
          textControls.set(finalPos);
          videoControls.set("visible");
          menuControls.set({
            opacity: 1,
            y: 0
          });
          return;
        }
        textControls.set({
          y: initialY,
          opacity: 1
        });
        videoControls.set("initial");
        menuControls.set({
          opacity: 0,
          y: 20
        });
        await nextFrame();
        if (cancelled) {
          return;
        }
        videoControls.start("visible");
        await textControls.start({
          ...finalPos,
          transition: {
            duration: 1.8,
            ease: [0.76, 0, 0.24, 1]
          }
        });
        if (cancelled) {
          return;
        }
        menuControls.start({
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          } as any
        });
        safeSessionSet(SESSION_KEY, "true");
      };
      runSequence();
      return () => {
        cancelled = true;
      };
    };
    t2 = [textControls, videoControls, menuControls];
    $[0] = menuControls;
    $[1] = textControls;
    $[2] = videoControls;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  if (!hero?.companyName) {
    return null;
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      y: "var(--hero-initial-y)",
      opacity: 1
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== hero.companyName) {
    t4 = <p className={H1_CLASS}>{hero.companyName}</p>;
    $[6] = hero.companyName;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      opacity: 0
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      visible: {
        opacity: 1
      }
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== hero.leftTitle || $[11] !== videoControls) {
    t7 = <motion.span initial={t5} animate={videoControls} variants={t6}>{hero.leftTitle}</motion.span>;
    $[10] = hero.leftTitle;
    $[11] = videoControls;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      opacity: 0
    };
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      visible: {
        opacity: 1
      }
    };
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== hero.rightTitle || $[16] !== videoControls) {
    t10 = <motion.span initial={t8} animate={videoControls} variants={t9}>{hero.rightTitle}</motion.span>;
    $[15] = hero.rightTitle;
    $[16] = videoControls;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t7) {
    t11 = <div className="w-full mt-2 px-[1.5%] max-w-[98vw]"><div className={TITLES_ROW_CLASS}>{t7}{t10}</div></div>;
    $[18] = t10;
    $[19] = t7;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t4 || $[23] !== textControls) {
    t12 = <motion.div initial={t3} animate={textControls} className={WRAPPER_CLASS}>{t4}{t11}</motion.div>;
    $[21] = t11;
    $[22] = t4;
    $[23] = textControls;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      opacity: 0,
      y: 20
    };
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ul className={UL_CLASS}>{MENU_ITEMS.map(_temp)}</ul>;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== menuControls) {
    t15 = <motion.nav className={NAV_CLASS} initial={t13} animate={menuControls}>{t14}</motion.nav>;
    $[27] = menuControls;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t12 || $[30] !== t15) {
    t16 = <div className={ROOT_CLASS}>{t12}{t15}</div>;
    $[29] = t12;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  return t16;
}
function _temp(item) {
  return <li key={item.href}><MenuButton href={item.href} label={item.label} /></li>;
}